import React, { useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useCreateData, useDeleteData, useFetchData } from '../../../hooks';
import {
  PlanningInputKind,
  PlanningInputsResponse,
  PlanningInputValue,
  PlanningProcessInput,
} from '../../../types';
import PageHeaderAction from '../../../components/PageHeaderAction';
import {
  convertDate,
  dateFormat,
  dateNameTimeFormat,
} from '../../../lib/utils';

const { Text, Title } = Typography;

const API = '/planning/inputs';

const SOURCE_COLOUR: Record<PlanningInputValue['source'], string> = {
  MANUAL: 'gold',
  'WIP-COMPUTED': 'geekblue',
};

const KIND_LABEL: Record<PlanningInputKind, string> = {
  ALLOC_WORKERS: 'Allocated workers',
  WORK_MINUTES_DAY: 'Working minutes per day',
  SHIP_GAP_DAYS: 'Ship gap (days)',
  LEAD_TIME_DAYS: 'Lead time (days)',
};

interface PlanningInputPayload {
  kind: PlanningInputKind;
  process: string | null;
  effectiveFrom: string;
  value: number;
  note: string | null;
}

interface ModalConfig {
  kind: PlanningInputKind;
  process: string | null;
  value?: number;
}

const NotSetUp = () => <Text type={'secondary'}>not set up</Text>;

const SourceTag: React.FC<{ v: PlanningInputValue }> = ({ v }) => (
  <Tag color={SOURCE_COLOUR[v.source]}>{v.source}</Tag>
);

const EnteredMeta: React.FC<{ v: PlanningInputValue }> = ({ v }) => (
  <Text type={'secondary'} className={'text-xs'}>
    from {convertDate(v.effectiveFrom, dateFormat)}
    {v.enteredBy ? ` · ${v.enteredBy}` : ''}
    {v.enteredAt ? ` · ${convertDate(v.enteredAt, dateNameTimeFormat)}` : ''}
  </Text>
);

/** embedded: shown as a tab of the Planning page, which carries the header. */
const PlanningInputsPage: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const { data, isLoading } = useFetchData<PlanningInputsResponse>(API);
  const create = useCreateData<PlanningInputPayload>(API);
  const del = useDeleteData(API);

  const [config, setConfig] = useState<ModalConfig | null>(null);
  const [form] = Form.useForm();

  const openModal = (c: ModalConfig) => {
    setConfig(c);
    form.resetFields();
    form.setFieldsValue({
      effectiveFrom: dayjs(),
      value: c.value,
      note: undefined,
    });
  };

  const onSubmit = async () => {
    if (!config) return;
    const values = await form.validateFields();
    const effective: Dayjs = values.effectiveFrom;
    // Allocations are weekly; the week picker returns a day inside the week.
    const effectiveFrom =
      config.kind === 'ALLOC_WORKERS'
        ? effective.startOf('week').format(dateFormat)
        : effective.format(dateFormat);
    const note: string | undefined = values.note;
    create.mutate(
      {
        kind: config.kind,
        process: config.process,
        effectiveFrom,
        value: values.value,
        note: note && note.trim() ? note.trim() : null,
      },
      {
        onSuccess: () => {
          message.success('Saved');
          setConfig(null);
        },
        onError: (e) =>
          message.error(e?.response?.data?.message ?? 'Save failed'),
      },
    );
  };

  const onDeactivate = (id: string) =>
    del.mutate(
      { id },
      {
        onSuccess: () => message.success('Allocation deactivated'),
        onError: (e) =>
          message.error(e?.response?.data?.message ?? 'Delete failed'),
      },
    );

  const shipGap = data?.shipGap;

  const columns = [
    {
      title: 'Process',
      dataIndex: 'process',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Allocated workers',
      key: 'alloc',
      render: (_: unknown, record: PlanningProcessInput) => (
        <Space direction={'vertical'} size={2} align={'start'}>
          {record.alloc.current ? (
            <>
              <Space size={4}>
                <Text strong>{record.alloc.current.value}</Text>
                <SourceTag v={record.alloc.current} />
              </Space>
              <EnteredMeta v={record.alloc.current} />
            </>
          ) : (
            <NotSetUp />
          )}
          <Button
            size={'small'}
            onClick={() =>
              openModal({ kind: 'ALLOC_WORKERS', process: record.process })
            }
          >
            Set allocation
          </Button>
        </Space>
      ),
    },
    {
      title: 'WIP actual',
      key: 'actual',
      render: (_: unknown, record: PlanningProcessInput) =>
        record.actual ? (
          <Space direction={'vertical'} size={0} align={'start'}>
            <Text>
              {record.actual.workers4wk ?? '—'} distinct ·{' '}
              {record.actual.avgPerDay ?? '—'}/day
            </Text>
            <Text type={'secondary'} className={'text-xs'}>
              last 4 weeks
              {record.actual.asOf
                ? ` · as of ${convertDate(record.actual.asOf, dateFormat)}`
                : ''}
            </Text>
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'Lead time (days)',
      key: 'leadTime',
      render: (_: unknown, record: PlanningProcessInput) => {
        const lt = record.leadTime;
        return (
          <Space direction={'vertical'} size={2} align={'start'}>
            {lt.resolved ? (
              <>
                <Space size={4}>
                  <Text strong>{lt.resolved.value}</Text>
                  <SourceTag v={lt.resolved} />
                </Space>
                {lt.computed && (
                  <Text type={'secondary'} className={'text-xs'}>
                    computed {lt.computed.value} (median of{' '}
                    {lt.computed.sampleSize ?? '—'} proders, last 12 months)
                  </Text>
                )}
                {lt.manual && (
                  <Text type={'secondary'} className={'text-xs'}>
                    manual override {lt.manual.value} · from{' '}
                    {convertDate(lt.manual.effectiveFrom, dateFormat)}
                  </Text>
                )}
              </>
            ) : (
              <NotSetUp />
            )}
            <Button
              size={'small'}
              onClick={() =>
                openModal({ kind: 'LEAD_TIME_DAYS', process: record.process })
              }
            >
              Override
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className={'page-wrapper'}>
      {!embedded && (
        <PageHeaderAction moduleName={'Planning Inputs'} action={null} />
      )}

      <div className={'my-2'}>
        {!embedded && (
          <Title level={3} style={{ margin: 0 }}>
            Planning Inputs
          </Title>
        )}
        {data?.computedAsOf ? (
          <Text type={'secondary'}>
            Computed nightly from PDMOPS WIP · as of{' '}
            {convertDate(data.computedAsOf, dateNameTimeFormat)}
          </Text>
        ) : (
          <Text type={'warning'}>
            Computed nightly from PDMOPS WIP · never computed yet
          </Text>
        )}
      </div>

      <Card size={'small'} title={'Pace'} className={'mb-3'}>
        <Space split={<Divider type={'vertical'} />} align={'start'} size={24}>
          <Space direction={'vertical'} size={2} align={'start'}>
            <Text strong>Working minutes per day</Text>
            {data?.workMinutesDay ? (
              <>
                <Space size={4}>
                  <Text strong style={{ fontSize: 18 }}>
                    {data.workMinutesDay.value}
                  </Text>
                  <SourceTag v={data.workMinutesDay} />
                </Space>
                <EnteredMeta v={data.workMinutesDay} />
                <Button
                  size={'small'}
                  onClick={() =>
                    openModal({
                      kind: 'WORK_MINUTES_DAY',
                      process: null,
                      value: data.workMinutesDay?.value,
                    })
                  }
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <NotSetUp />
                <Button
                  size={'small'}
                  onClick={() =>
                    openModal({
                      kind: 'WORK_MINUTES_DAY',
                      process: null,
                      value: 450,
                    })
                  }
                >
                  Use 450 (WIP standard 7.5 h)
                </Button>
              </>
            )}
          </Space>

          <Space direction={'vertical'} size={2} align={'start'}>
            <Text strong>Ship gap (days)</Text>
            {shipGap?.resolved ? (
              <Space size={4}>
                <Text strong style={{ fontSize: 18 }}>
                  {shipGap.resolved.value}
                </Text>
                <SourceTag v={shipGap.resolved} />
              </Space>
            ) : (
              <NotSetUp />
            )}
            {shipGap?.computed && (
              <Text type={'secondary'} className={'text-xs'}>
                computed {shipGap.computed.value} · median of{' '}
                {shipGap.computed.sampleSize ?? '—'} proders
              </Text>
            )}
            {shipGap?.manual && (
              <Text type={'secondary'} className={'text-xs'}>
                manual override {shipGap.manual.value} · from{' '}
                {convertDate(shipGap.manual.effectiveFrom, dateFormat)}
              </Text>
            )}
            <Button
              size={'small'}
              onClick={() =>
                openModal({ kind: 'SHIP_GAP_DAYS', process: null })
              }
            >
              Override
            </Button>
          </Space>
        </Space>
      </Card>

      <Table<PlanningProcessInput>
        rowKey={(record) => record.process}
        dataSource={data?.processes ?? []}
        loading={isLoading}
        columns={columns}
        size={'small'}
        pagination={false}
        expandable={{
          rowExpandable: (record) => record.alloc.history.length > 0,
          expandedRowRender: (record) => (
            <Table<PlanningInputValue>
              rowKey={(v) => v.id}
              dataSource={record.alloc.history}
              size={'small'}
              pagination={false}
              columns={[
                { title: 'Workers', dataIndex: 'value' },
                {
                  title: 'Effective from',
                  dataIndex: 'effectiveFrom',
                  render: (v: string) => convertDate(v, dateFormat),
                },
                {
                  title: 'Entered by',
                  dataIndex: 'enteredBy',
                  render: (v: string | null) => v ?? '—',
                },
                {
                  title: 'Entered at',
                  dataIndex: 'enteredAt',
                  render: (v: string | null) =>
                    v ? convertDate(v, dateNameTimeFormat) : '—',
                },
                {
                  title: 'Note',
                  dataIndex: 'note',
                  render: (v: string | null) => v ?? '—',
                },
                {
                  title: '',
                  key: 'actions',
                  render: (_: unknown, v: PlanningInputValue) =>
                    v.id ? (
                      <Popconfirm
                        title={'Deactivate this allocation?'}
                        onConfirm={() => onDeactivate(v.id)}
                      >
                        <Button size={'small'} danger>
                          Deactivate
                        </Button>
                      </Popconfirm>
                    ) : null,
                },
              ]}
            />
          ),
        }}
      />

      <Modal
        title={
          config
            ? `${KIND_LABEL[config.kind]}${
                config.process ? ` · ${config.process}` : ''
              }`
            : 'Planning input'
        }
        open={config !== null}
        onOk={onSubmit}
        onCancel={() => setConfig(null)}
        confirmLoading={create.isPending}
      >
        <Form form={form} layout={'vertical'}>
          <Form.Item
            name={'effectiveFrom'}
            label={
              config?.kind === 'ALLOC_WORKERS'
                ? 'Effective from (week)'
                : 'Effective from'
            }
            rules={[{ required: true, message: 'Effective date is required' }]}
          >
            <DatePicker
              picker={config?.kind === 'ALLOC_WORKERS' ? 'week' : 'date'}
              allowClear={false}
            />
          </Form.Item>
          <Form.Item
            name={'value'}
            label={'Value'}
            rules={[{ required: true, message: 'Value is required' }]}
          >
            <InputNumber
              min={1}
              precision={config?.kind === 'LEAD_TIME_DAYS' ? 1 : 0}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name={'note'} label={'Note'}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PlanningInputsPage;
